import { useCallback, useState } from 'react'

import { useUserAuthToken } from 'Hooks'

import { handleFetchErrorFunctional } from 'Utilities'

import { SentryErrorType } from 'Constants/errors'

const CONTENT_DISPOSITION_FILENAME_RE = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i

const parseContentDispositionFilename = (
  header: string | null,
): string | null => {
  if (!header) {
    return null
  }

  const match = CONTENT_DISPOSITION_FILENAME_RE.exec(header)

  return match ? decodeURIComponent(match[1]) : null
}

const EXTENSION_BY_CONTENT_TYPE: Record<string, string> = {
  'application/pdf': 'pdf',
  'image/gif': 'gif',
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/tiff': 'tif',
}

/**
 * Only reached when Content-Disposition is unreadable - e.g. an environment
 * where the API does not expose the header to cross-origin fetch(). Carriers
 * hand back GIF labels as well as PDF ones, so the extension follows the bytes
 * we actually received instead of assuming a PDF.
 */
const buildFallbackFilename = (
  trackingReference: string,
  contentType: string,
): string => {
  const mediaType = contentType.split(';')[0].trim().toLowerCase()
  const extension = EXTENSION_BY_CONTENT_TYPE[mediaType] ?? 'pdf'

  return `return-label-${trackingReference}.${extension}`
}

const triggerBrowserDownload = (objectUrl: string, filename: string) => {
  const anchor = document.createElement('a')

  anchor.href = objectUrl
  anchor.download = filename
  anchor.rel = 'noopener noreferrer'
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
}

interface UsePortalReturnLabelDownloadResult {
  download: () => Promise<void>
  isLoading: boolean
  error: Error | null
}

export const usePortalReturnLabelDownload = (
  trackingReference: string | null | undefined,
  organisationId: string | null | undefined,
  pdfDownloadUrl?: string | null,
): UsePortalReturnLabelDownloadResult => {
  const authToken = useUserAuthToken()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const download = useCallback(async () => {
    if (!trackingReference || !organisationId) {
      return
    }

    if (!authToken) {
      setError(new Error('Authentication token missing'))

      return
    }

    setIsLoading(true)
    setError(null)

    // The BE-built URL is the source of truth; origin/security enforcement
    // belongs to BE (see ReturnLabelDownloadURL + TokenAuthMiddleware in
    // cloud-run). FE forwards the JWT to whatever URL the resolver returns.
    // Fallback covers dev envs where API_HOST is unset and the field is null.
    const url =
      pdfDownloadUrl ??
      `${window.GO_API_URL}/api/v2/return-label/${encodeURIComponent(
        trackingReference,
      )}/download?organisationId=${encodeURIComponent(organisationId)}`

    try {
      const response = await fetch(url, {
        headers: { Authorization: authToken },
      })

      if (!response.ok) {
        throw new Error(`Download failed with status ${response.status}`)
      }

      const blob = await response.blob()
      const objectUrl = URL.createObjectURL(blob)
      const filename =
        parseContentDispositionFilename(
          response.headers.get('content-disposition'),
        ) ?? buildFallbackFilename(trackingReference, blob.type)

      triggerBrowserDownload(objectUrl, filename)
      // Defer revoke so Safari and WebView can begin reading the stream
      // before the URL is invalidated.
      setTimeout(() => {
        URL.revokeObjectURL(objectUrl)
      }, 0)
    } catch (caughtError) {
      const downloadError =
        caughtError instanceof Error
          ? caughtError
          : new Error('Download failed')

      handleFetchErrorFunctional()(downloadError, {
        operation: {
          operationName: 'usePortalReturnLabelDownload',
          variables: { organisationId, trackingReference },
        },
        type: SentryErrorType.API_ERROR,
      })
      setError(downloadError)
    } finally {
      setIsLoading(false)
    }
  }, [authToken, organisationId, pdfDownloadUrl, trackingReference])

  return { download, error, isLoading }
}
